import { APP_INITIALIZER, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, Provider } from '@angular/core';
import { COMPANIES, Company, EnvironmentAndConfiguration, RuntimeEnvironment } from '@lx/shared/types';
import { StoryContext } from '@storybook/angular';
import { getApiFromEnvironment } from './api.functions';
import { StorybookConfigService } from '../services/storybook-config.service';
import { BASE_PATH } from '@lx/shared/api/variables';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { APP_NAME, COMPANY, getCompanyHeader, getEnvironment, PATH_PREFIX } from '@lx/shared/tokens';
import {
  configFactory,
  CONFIGURATION_PROVIDER,
  CustomisedNGXLoggerServerService,
  getEnvironmentProvider,
  localeFactory,
  registerLocale,
} from '@lx/shared/app-init';
import { APPLICATION_CONFIGURATION_SERVICE, BaseConfigService } from '@lx/shared/app-config';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import { GlobalErrorHandler, HttpErrorInterceptor, httpErrorInterceptorFn, WHITELISTED_ERROR_CODES } from '@lx/shared/errors';
import { provideNotificationService, ToastNotificationService } from '@lx/shared/logger';
import { LoggerModule, NGXLogger, NgxLoggerLevel, TOKEN_LOGGER_SERVER_SERVICE } from 'ngx-logger';
import { loggingFactory } from '@lx/shared/app-init/functions/logging.factory';
import { Routes } from '@angular/router';

export const mockRoutes: Routes = [
  { path: '', redirectTo: '/', pathMatch: 'full' },
  { path: 'iframe.html', redirectTo: '/', pathMatch: 'full' },
];

export function buildCoreEnvironmentFromContext(context: StoryContext): RuntimeEnvironment {
  const { locale, company, environment } = context.globals;
  const { value: apiPath } = getApiFromEnvironment(environment);
  return {
    production: environment === 'production',
    environment,
    API_BASE_PATH: apiPath,
    LOCALE: locale,
    PATH_PREFIX: undefined,
    PUBLIC_PATH: '',
    DIST_PATH: '',
    I18N_PATH: 'assets/translations',
    COMPANY: company,
    RTL_ENABLED: false,
    FLYWIRE_DEPOSITS_RETURN_URL: '',
  };
}

function getPathPrefix(context: StoryContext) {
  const { company, locale } = context.globals;
  switch (company) {
    case COMPANIES.ALPADIA:
      return `https://www.alpadia.com/${locale}`;
    case COMPANIES.ESL:
      return `https://www.esl-languages.com/${locale}`;
    case COMPANIES.KAPLAN:
      return `https://www.kaplaninternational.com/${locale}`;
    case COMPANIES.POSITIVELY_LANGUAGES: // Todo to use a real value when we have one
      return `https://www.positivelylanguages.com/${locale}`;
  }
}

export function getContextProviders(context: StoryContext, company: Company | null = null): Provider[] {
  const { company: contextCompany, locale } = context.globals;
  return [
    MessageService,
    { provide: COMPANY, useValue: company ?? contextCompany ?? COMPANIES.KAPLAN },
    { provide: LOCALE_ID, useValue: locale },
    { provide: PATH_PREFIX, useValue: getPathPrefix(context) },
    { provide: APP_NAME, useValue: 'story-book' },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const locale = inject(LOCALE_ID);
        const primeNGConfig = inject(PrimeNGConfig);
        registerLocale(locale);
        return localeFactory(primeNGConfig);
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        const loggingService = inject(NGXLogger);
        const companyHeader = getCompanyHeader();
        const env = buildCoreEnvironmentFromContext(context);
        return loggingFactory(configService, loggingService, companyHeader, env);
      },
      multi: true,
    },
  ];
}

export function getAsyncProviders(context: StoryContext): Provider[] {
  return [
    { provide: APPLICATION_CONFIGURATION_SERVICE, useClass: StorybookConfigService },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const configService = inject(APPLICATION_CONFIGURATION_SERVICE);
        return initializeStory(context, configService);
      },
      multi: true,
    },
  ];
}

export function initializeStory(context: StoryContext, configService: BaseConfigService): () => Promise<EnvironmentAndConfiguration> {
  return configFactory(buildCoreEnvironmentFromContext(context), configService);
}

export function getProvidersFromStorybookContext(context: StoryContext, onlyContextProviders = false, company: Company | null = null): Provider[] {
  let providers: Provider[] = getContextProviders(context, company);
  if (onlyContextProviders) {
    return providers;
  }

  providers = providers.concat(...getAsyncProviders(context));
  providers = providers.concat([
    importProvidersFrom(
      LoggerModule.forRoot(
        {
          level: NgxLoggerLevel.DEBUG,
          disableConsoleLogging: false,
          serverLogLevel: NgxLoggerLevel.ERROR,
        },
        {
          serverProvider: {
            provide: TOKEN_LOGGER_SERVER_SERVICE,
            useClass: CustomisedNGXLoggerServerService,
          },
        },
      ),
    ),
    getEnvironmentProvider(buildCoreEnvironmentFromContext(context)),
    CONFIGURATION_PROVIDER,
    {
      provide: BASE_PATH,
      useFactory: () => {
        const { API_BASE_PATH } = getEnvironment();
        return API_BASE_PATH;
      },
    },
    getErrorHandlerProviders([410, 404]),
  ]);

  return providers;
}

export function getHttpProviderForStorybook() {
  return provideHttpClient(withInterceptors([httpErrorInterceptorFn]));
}

export function getErrorHandlerProviders(whiteListErrorCodes = []) {
  return [
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: WHITELISTED_ERROR_CODES, useValue: whiteListErrorCodes },
    provideNotificationService(ToastNotificationService),
  ];
}
